import React from 'react'
import type { UseFormReturn } from 'react-hook-form'

import type { BookingMasterData } from 'repositories/BerthApi/getBookingMasterData/type'
import type { Booking } from 'repositories/BerthApi/types/booking'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'

import type { useMasterIntegration } from '../../../hooks/useMasterIntegration'
import type { Berth } from '../../../types'
import {
  ADMIN_NOTE_MAX_LENGTH,
  EditBookingFormValues,
  EditBookingResolverContext,
} from '../../../utils/editBookingValidation'
import { Button } from '../../Button'
import { CompanyComboBox } from '../ComboBoxes/CompanyComboBox'
import { DriverComboBox } from '../ComboBoxes/DriverComboBox'
import { VehicleControllerComboBox } from '../ComboBoxes/VehicleControllerComboBox'
import { VehicleNumberComboBox } from '../ComboBoxes/VehicleNumberComboBox'

import { AttachmentSection } from './AttachmentSection'
import { CargoSection } from './CargoSection'
import { EntryInstructionSection } from './EntryInstructionSection'
import styles from './index.module.css'
import { buildTimeOptions, computeWorkingTimeLabel } from './util'

type Props = {
  form: UseFormReturn<EditBookingFormValues, EditBookingResolverContext>
  booking: Booking | null
  berths: Berth[]
  tenantId: string | null
  warehouseId: string | null
  isUnassigned: boolean
  isSubmittingBasic: boolean
  isSubmittingAdminNote: boolean
  isSubmittingEntryInstruction: boolean
  isBasicDirty: boolean
  isAdminNoteDirty: boolean
  isEntryInstructionDirty: boolean
  isFinish: boolean
  repeatLabel: string
  masterData: BookingMasterData | null
  masterIntegration: ReturnType<typeof useMasterIntegration>
  onSubmitBasic: () => void
  onSubmitAdminNote: () => void
  onSubmitEntryInstruction: () => void
  onNotify: (payload: SnackbarPayload) => void
}

export const EditBookingForm: React.FC<Props> = ({
  form,
  booking,
  berths,
  tenantId,
  warehouseId,
  isUnassigned,
  isSubmittingBasic,
  isSubmittingAdminNote,
  isSubmittingEntryInstruction,
  isBasicDirty,
  isAdminNoteDirty,
  isEntryInstructionDirty,
  isFinish,
  repeatLabel,
  masterData,
  masterIntegration,
  onSubmitBasic,
  onSubmitAdminNote,
  onSubmitEntryInstruction,
  onNotify,
}) => {
  const vehicleCategories = masterData?.vehicleCategories ?? []
  const isEntryInstructionTimeDisplayed = !!masterData?.tenantParameters.base.some(
    (p) => p.columnName === 'entry_instruction_time' && p.displayFlag
  )
  const showEntryInstructionSection =
    isEntryInstructionTimeDisplayed && !booking?.delegationId && !!booking?.bookingId
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form
  const values = watch()

  const isBasicDisabled = isFinish || isSubmittingBasic
  // 添付資料は予約情報セクションのサブセクション（荷積みの下）。非委譲・booking 確定済みのとき表示し、
  // 予約情報の保存ボタンで一括保存する（未割り当て予約も対象 ＝ PC 版互換）。
  const showAttachmentSection = !booking?.delegationId && !!booking?.bookingId

  return (
    <div className={styles.form}>
      {/* ===== 予約情報 ===== */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>予約情報</div>

        {/* 基本情報 */}
        <div className={styles.subSection}>
          <div className={styles.subSectionLabel}>基本情報</div>
          <div className={styles.fieldList}>
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="startDate">
                  開始日<span className={styles.required}>*</span>
                </label>
                <input
                  id="startDate"
                  type="date"
                  className={styles.input}
                  disabled={isBasicDisabled}
                  {...register('startDate')}
                />
                {errors.startDate && (
                  <span className={styles.errorText}>{errors.startDate.message}</span>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="startTime">
                  開始時刻<span className={styles.required}>*</span>
                </label>
                <select
                  id="startTime"
                  className={styles.timeSelect}
                  disabled={isBasicDisabled}
                  {...register('startTime')}
                >
                  <option value="">--:--</option>
                  {buildTimeOptions(values.startTime).map((t) => (
                    <option
                      key={t}
                      value={t}
                      style={t.endsWith(':00') ? { fontWeight: 700 } : undefined}
                    >
                      {t}
                    </option>
                  ))}
                </select>
                {errors.startTime && (
                  <span className={styles.errorText}>{errors.startTime.message}</span>
                )}
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="endDate">
                  終了日<span className={styles.required}>*</span>
                </label>
                <input
                  id="endDate"
                  type="date"
                  className={styles.input}
                  disabled={isBasicDisabled}
                  {...register('endDate')}
                />
                {errors.endDate && (
                  <span className={styles.errorText}>{errors.endDate.message}</span>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="endTime">
                  終了時刻<span className={styles.required}>*</span>
                </label>
                <select
                  id="endTime"
                  className={styles.timeSelect}
                  disabled={isBasicDisabled}
                  {...register('endTime')}
                >
                  <option value="">--:--</option>
                  {buildTimeOptions(values.endTime).map((t) => (
                    <option
                      key={t}
                      value={t}
                      style={t.endsWith(':00') ? { fontWeight: 700 } : undefined}
                    >
                      {t}
                    </option>
                  ))}
                </select>
                {errors.endTime && (
                  <span className={styles.errorText}>{errors.endTime.message}</span>
                )}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="workingTime">
                作業時間
              </label>
              <input
                id="workingTime"
                type="text"
                className={styles.input}
                value={computeWorkingTimeLabel(values)}
                disabled
                readOnly
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="berthId">
                バース
                {!isUnassigned && <span className={styles.required}>*</span>}
              </label>
              <select
                id="berthId"
                className={styles.select}
                disabled={isBasicDisabled}
                {...register('berthId')}
              >
                <option value="">{isUnassigned ? '未割当' : '未選択'}</option>
                {berths.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {errors.berthId && <span className={styles.errorText}>{errors.berthId.message}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="repeatLabel">
                繰り返し
              </label>
              <input
                id="repeatLabel"
                type="text"
                className={styles.input}
                value={repeatLabel}
                disabled
                readOnly
              />
            </div>
          </div>
        </div>

        <hr className={styles.divider} />

        {/* 配車担当 */}
        <div className={styles.subSection}>
          <div className={styles.subSectionLabel}>配車担当</div>
          <div className={styles.fieldList}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="companyName">
                会社名
              </label>
              <CompanyComboBox
                id="companyName"
                tenantId={tenantId}
                value={values.companyName ?? ''}
                onChange={({ label, value }) => {
                  const companyChanged =
                    label !== (values.companyName ?? '') || value !== values.companyId
                  if (companyChanged) {
                    setValue('vehicleControllerName', '', { shouldDirty: true })
                    setValue('vehicleControllerId', null, { shouldDirty: true })
                    setValue('vehicleControllerPhone', '', { shouldDirty: true })
                    setValue('vehicleControllerEmail', '', { shouldDirty: true })
                  }
                  setValue('companyName', label, { shouldDirty: true })
                  setValue('companyId', value, { shouldDirty: true })
                }}
                disabled={isBasicDisabled}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="vehicleControllerName">
                配車担当者名
              </label>
              <VehicleControllerComboBox
                id="vehicleControllerName"
                tenantId={tenantId}
                warehouseId={warehouseId}
                companyId={values.companyId}
                value={values.vehicleControllerName ?? ''}
                onChange={({ label, value }) => {
                  if (values.vehicleControllerId != null && value !== values.vehicleControllerId) {
                    setValue('vehicleControllerPhone', '', { shouldDirty: true })
                    setValue('vehicleControllerEmail', '', { shouldDirty: true })
                  }
                  setValue('vehicleControllerName', label, { shouldDirty: true })
                  setValue('vehicleControllerId', value, { shouldDirty: true })
                }}
                onSelectMaster={({ phone, email }) => {
                  setValue('vehicleControllerPhone', phone ?? '', { shouldDirty: true })
                  setValue('vehicleControllerEmail', email ?? '', { shouldDirty: true })
                }}
                disabled={isBasicDisabled || !values.companyName}
              />
            </div>

            {values.vehicleControllerName && (
              <>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="vehicleControllerPhone">
                    電話番号
                  </label>
                  <input
                    id="vehicleControllerPhone"
                    type="text"
                    className={styles.input}
                    inputMode="tel"
                    maxLength={11}
                    autoComplete="off"
                    disabled={isBasicDisabled || values.vehicleControllerId != null}
                    {...register('vehicleControllerPhone', {
                      onChange: (e) => {
                        const sanitized = e.target.value.replace(/\D/g, '').slice(0, 11)
                        if (sanitized !== e.target.value) {
                          setValue('vehicleControllerPhone', sanitized, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                      },
                    })}
                  />
                  {errors.vehicleControllerPhone && (
                    <span className={styles.errorText}>
                      {errors.vehicleControllerPhone.message}
                    </span>
                  )}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="vehicleControllerEmail">
                    メールアドレス<span className={styles.required}>*</span>
                  </label>
                  <input
                    id="vehicleControllerEmail"
                    type="text"
                    className={styles.input}
                    inputMode="email"
                    autoComplete="off"
                    disabled={isBasicDisabled || values.vehicleControllerId != null}
                    {...register('vehicleControllerEmail', {
                      onBlur: (e) => {
                        masterIntegration.handleControllerEmailBlur(e.target.value)
                      },
                    })}
                  />
                  {errors.vehicleControllerEmail && (
                    <span className={styles.errorText}>
                      {errors.vehicleControllerEmail.message}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <hr className={styles.divider} />

        {/* 運送会社 */}
        <div className={styles.subSection}>
          <div className={styles.subSectionLabel}>運送会社</div>
          <div className={styles.fieldList}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="driverCompanyName">
                運送会社名
              </label>
              <CompanyComboBox
                id="driverCompanyName"
                tenantId={tenantId}
                value={values.driverCompanyName ?? ''}
                onChange={({ label, value }) => {
                  const driverCompanyChanged =
                    label !== (values.driverCompanyName ?? '') || value !== values.driverCompanyId
                  if (driverCompanyChanged) {
                    setValue('vehicleNumber', '', { shouldDirty: true })
                    setValue('vehicleCategory', '', { shouldDirty: true })
                    setValue('driverName', '', { shouldDirty: true })
                    setValue('driverId', null, { shouldDirty: true })
                    setValue('driverPhone', '', { shouldDirty: true })
                    setValue('driverEmail', '', { shouldDirty: true })
                  }
                  setValue('driverCompanyName', label, { shouldDirty: true })
                  setValue('driverCompanyId', value, { shouldDirty: true })
                }}
                disabled={isBasicDisabled}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="vehicleNumber">
                車両番号
              </label>
              <VehicleNumberComboBox
                id="vehicleNumber"
                tenantId={tenantId}
                warehouseId={warehouseId}
                driverCompanyId={values.driverCompanyId}
                value={values.vehicleNumber ?? ''}
                onChange={({ label }) => {
                  setValue('vehicleNumber', label, { shouldDirty: true })
                }}
                disabled={isBasicDisabled || !values.driverCompanyName}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="vehicleCategory">
                車両形態
              </label>
              <select
                id="vehicleCategory"
                className={styles.select}
                disabled={isBasicDisabled || !values.driverCompanyName}
                {...register('vehicleCategory')}
              >
                <option value="">未選択</option>
                {vehicleCategories
                  .filter(
                    (vc) =>
                      !!vc.parameterName &&
                      (vc.displayFlag || vc.parameterName === values.vehicleCategory)
                  )
                  .map((vc) => (
                    <option key={vc.parameterId} value={vc.parameterName ?? ''}>
                      {vc.parameterName}
                    </option>
                  ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="driverName">
                ドライバー名
              </label>
              <DriverComboBox
                id="driverName"
                tenantId={tenantId}
                warehouseId={warehouseId}
                driverCompanyId={values.driverCompanyId}
                value={values.driverName ?? ''}
                onChange={({ label, value }) => {
                  if (values.driverId != null && value !== values.driverId) {
                    setValue('driverPhone', '', { shouldDirty: true })
                    setValue('driverEmail', '', { shouldDirty: true })
                  }
                  setValue('driverName', label, { shouldDirty: true })
                  setValue('driverId', value, { shouldDirty: true })
                }}
                onSelectMaster={({ phone, email }) => {
                  setValue('driverPhone', phone ?? '', { shouldDirty: true })
                  setValue('driverEmail', email ?? '', { shouldDirty: true })
                }}
                disabled={isBasicDisabled || !values.driverCompanyName}
              />
            </div>

            {values.driverName && (
              <>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="driverPhone">
                    電話番号<span className={styles.required}>*</span>
                  </label>
                  <input
                    id="driverPhone"
                    type="text"
                    className={styles.input}
                    inputMode="tel"
                    maxLength={11}
                    autoComplete="off"
                    disabled={isBasicDisabled || values.driverId != null}
                    {...register('driverPhone', {
                      onChange: (e) => {
                        const raw = e.target.value
                        const sanitized = raw.replace(/\D/g, '').slice(0, 11)
                        if (sanitized !== raw) {
                          setValue('driverPhone', sanitized, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        masterIntegration.handleDriverPhoneChange(sanitized, values.driverName)
                      },
                    })}
                  />
                  {errors.driverPhone && (
                    <span className={styles.errorText}>{errors.driverPhone.message}</span>
                  )}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="driverEmail">
                    メールアドレス
                  </label>
                  <input
                    id="driverEmail"
                    type="text"
                    className={styles.input}
                    inputMode="email"
                    autoComplete="off"
                    disabled={isBasicDisabled || values.driverId != null}
                    {...register('driverEmail')}
                  />
                  {errors.driverEmail && (
                    <span className={styles.errorText}>{errors.driverEmail.message}</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <hr className={styles.divider} />

        {/* 荷降ろし */}
        <CargoSection
          form={form}
          masterData={masterData}
          side="import"
          isBasicDisabled={isBasicDisabled}
        />

        <hr className={styles.divider} />

        {/* 荷積み */}
        <CargoSection
          form={form}
          masterData={masterData}
          side="export"
          isBasicDisabled={isBasicDisabled}
        />

        {/* 添付資料（荷積みの下のサブセクション。予約情報の保存ボタンで一括保存） */}
        {showAttachmentSection && (
          <>
            <hr className={styles.divider} />
            <div className={styles.subSection}>
              <div className={styles.subSectionLabel}>添付資料</div>
              <AttachmentSection
                form={form}
                booking={booking}
                disabled={isBasicDisabled}
                onNotify={onNotify}
              />
            </div>
          </>
        )}

        <hr className={styles.divider} />

        <div className={styles.sectionFooter}>
          <div className={styles.saveButtonSpacer} aria-hidden="true" />
          <div className={styles.saveButtonWrapper}>
            <Button
              label="予約情報を保存"
              onClick={onSubmitBasic}
              disabled={isBasicDisabled || !isBasicDirty}
              variant="primary"
            />
          </div>
        </div>
      </div>

      {showEntryInstructionSection && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>
            入場指示
            <span className={styles.sectionNote}>※完了後でも編集可能</span>
          </div>
          <EntryInstructionSection form={form} disabled={isSubmittingEntryInstruction} />
          <div className={styles.sectionFooter}>
            <div className={styles.saveButtonSpacer} aria-hidden="true" />
            <div className={styles.saveButtonWrapper}>
              <Button
                label="入場指示を保存"
                onClick={onSubmitEntryInstruction}
                disabled={isSubmittingEntryInstruction || !isEntryInstructionDirty}
                variant="primary"
              />
            </div>
          </div>
        </div>
      )}

      {/* ===== 管理者備考 ===== */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>
          管理者備考
          <span className={styles.sectionNote}>※完了後でも編集可能</span>
        </div>
        <div className={styles.fieldList}>
          <div className={styles.field}>
            <textarea
              id="adminNote"
              className={styles.textarea}
              disabled={isSubmittingAdminNote}
              rows={3}
              maxLength={ADMIN_NOTE_MAX_LENGTH}
              {...register('adminNote')}
            />
            <span className={styles.charCount} aria-live="polite">
              {(values.adminNote ?? '').length}/{ADMIN_NOTE_MAX_LENGTH}文字
            </span>
            {errors.adminNote && (
              <span className={styles.errorText}>{errors.adminNote.message}</span>
            )}
          </div>
        </div>

        <div className={styles.sectionFooter}>
          <div className={styles.saveButtonSpacer} aria-hidden="true" />
          <div className={styles.saveButtonWrapper}>
            <Button
              label="管理者備考を保存"
              onClick={onSubmitAdminNote}
              disabled={isSubmittingAdminNote || !isAdminNoteDirty}
              variant="primary"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
